import React from 'react';
import { Landmark } from 'lucide-react';

// ─── Keyframes (prefijo fb- para no colisionar) ───
const feriadoStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&display=swap');

  @keyframes fb-enter {
    0%   { opacity: 0; transform: translateY(16px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes fb-halo {
    0%, 100% { opacity: 0.35; transform: scale(1); }
    50%      { opacity: 0.6;  transform: scale(1.08); }
  }
  @keyframes fb-ring-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes fb-sweep {
    0%   { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
    12%  { opacity: 0.55; }
    35%  { opacity: 0; }
    100% { transform: translateX(220%) skewX(-18deg); opacity: 0; }
  }
  @keyframes fb-rule {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
  @keyframes fb-medal-in {
    0%   { opacity: 0; transform: scale(0.82) rotate(-8deg); }
    100% { opacity: 1; transform: scale(1) rotate(0deg); }
  }
  @media (prefers-reduced-motion: reduce) {
    .fb-card *, .fb-card { animation: none !important; }
  }
`;

const capitalizar = (texto = '') =>
  texto.charAt(0).toUpperCase() + texto.slice(1);

const FeriadoBancarioCard = ({ data }) => {
  if (!data) return null;
  const { fecha_texto, descripcion, es_hoy } = data;

  const oro = '#c9a24a';
  const oroClaro = '#e6cd8f';

  return (
    <div
      className="fb-card relative overflow-hidden rounded-2xl shadow-sm border"
      style={{
        borderColor: 'rgba(201,162,74,0.35)',
        background:
          'linear-gradient(135deg, #0a1e3a 0%, #0e2747 48%, #0a1c36 100%)',
        animation: 'fb-enter 0.7s cubic-bezier(0.16,1,0.3,1) both',
      }}
    >
      <style>{feriadoStyles}</style>

      {/* Guilloche / papel de seguridad: anillos concentricos sutiles */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-radial-gradient(circle at 14% 50%, transparent 0 9px, rgba(201,162,74,0.05) 9px 10px)',
          opacity: 0.7,
        }}
      />
      {/* Vineta para dar profundidad */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(120% 120% at 85% 110%, rgba(0,0,0,0.35) 0%, transparent 55%)',
        }}
      />
      {/* Destello que cruza el papel */}
      <div
        className="absolute inset-y-0 pointer-events-none"
        style={{
          width: '45%',
          left: 0,
          background:
            'linear-gradient(90deg, transparent, rgba(230,205,143,0.18), transparent)',
          animation: 'fb-sweep 7.5s ease-in-out 1.2s infinite',
        }}
      />

      {/* Esquinas art-deco doradas */}
      {[
        { top: 14, left: 14, bt: true, bl: true },
        { top: 14, right: 14, bt: true, br: true },
        { bottom: 14, left: 14, bb: true, bl: true },
        { bottom: 14, right: 14, bb: true, br: true },
      ].map((c, i) => (
        <div
          key={i}
          className="absolute pointer-events-none"
          style={{
            width: 16,
            height: 16,
            top: c.top,
            bottom: c.bottom,
            left: c.left,
            right: c.right,
            borderTop: c.bt ? `1.5px solid ${oro}` : 'none',
            borderBottom: c.bb ? `1.5px solid ${oro}` : 'none',
            borderLeft: c.bl ? `1.5px solid ${oro}` : 'none',
            borderRight: c.br ? `1.5px solid ${oro}` : 'none',
            opacity: 0.5,
          }}
        />
      ))}

      {/* Contenido */}
      <div className="relative flex flex-col sm:flex-row items-center gap-5 sm:gap-7 px-6 sm:px-9 py-7 sm:py-8">

        {/* Medallon BCV */}
        <div
          className="relative flex-shrink-0"
          style={{ width: 104, height: 104, animation: 'fb-medal-in 0.8s cubic-bezier(0.16,1,0.3,1) 0.15s both' }}
        >
          {/* Halo que respira */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(circle, ${oro}55 0%, transparent 70%)`,
              filter: 'blur(8px)',
              animation: 'fb-halo 4.5s ease-in-out infinite',
            }}
          />
          {/* Anillo decorativo girando lento */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: `1.5px dashed ${oro}`,
              opacity: 0.45,
              animation: 'fb-ring-spin 48s linear infinite',
            }}
          />
          {/* Disco marfil con el sello */}
          <div
            className="absolute rounded-full flex items-center justify-center"
            style={{
              inset: 9,
              background: 'radial-gradient(circle at 38% 32%, #fdfaf2, #efe6d2)',
              boxShadow: `inset 0 1px 3px rgba(0,0,0,0.15), 0 0 0 2px ${oro}`,
            }}
          >
            <img
              src="/spublicaciones/feriados/bcv-logo.png"
              alt="Banco Central de Venezuela"
              style={{ width: '74%', height: '74%', objectFit: 'contain' }}
            />
          </div>
        </div>

        {/* Texto */}
        <div className="flex-1 text-center sm:text-left min-w-0">
          {/* Eyebrow */}
          <div
            className="flex items-center justify-center sm:justify-start gap-2 mb-2"
            style={{ color: oroClaro }}
          >
            <Landmark size={15} strokeWidth={2} />
            <span
              className="text-[11px] font-semibold uppercase"
              style={{ letterSpacing: '0.22em' }}
            >
              {es_hoy ? 'Hoy · Feriado Bancario' : 'Aviso · Feriado Bancario'}
            </span>
          </div>

          {/* Fecha (hero) */}
          <h3
            className="text-white leading-tight"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(1.5rem, 2.6vw, 2rem)',
              fontWeight: 600,
            }}
          >
            {capitalizar(fecha_texto)}
          </h3>

          {/* Regla dorada animada */}
          <div
            className="mx-auto sm:mx-0 mt-2 mb-3"
            style={{
              width: 72,
              height: 2,
              background: `linear-gradient(90deg, ${oro}, transparent)`,
              transformOrigin: 'left',
              animation: 'fb-rule 0.9s cubic-bezier(0.16,1,0.3,1) 0.5s both',
            }}
          />

          {/* Mensaje */}
          <p
            className="text-[14.5px] leading-relaxed"
            style={{ color: '#cdd8e8', maxWidth: '44rem' }}
          >
            Les informamos que el día{' '}
            <strong className="text-white font-semibold">{fecha_texto}</strong>{' '}
            es feriado bancario. Tomar las medidas pertinentes ante esta eventualidad.
          </p>

          {descripcion && (
            <p className="mt-2.5 text-[12px]" style={{ color: 'rgba(201,162,74,0.9)' }}>
              <span className="font-semibold uppercase" style={{ letterSpacing: '0.12em' }}>
                Motivo:
              </span>{' '}
              <span className="italic">{descripcion}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeriadoBancarioCard;
